/**
 * useUserCommunities Hook
 *
 * Custom hook for fetching user communities with infinite scroll
 * Uses React Query infinite queries for pagination
 */

import { useInfiniteQuery } from "@tanstack/react-query"
import { getUserCommunities } from "../services/community.service"
import type { UserCommunitiesFilters } from "../types/index"

export interface UseUserCommunitiesOptions {
  enabled?: boolean
}

/**
 * Hook to fetch user communities with infinite scroll
 * @param userId - The user ID to fetch communities for
 * @param filters - Optional filters (search, filter, sort)
 * @param options - Optional configuration
 * @returns React Query infinite query result
 */
export function useUserCommunities(
  userId: string,
  filters: UserCommunitiesFilters = {},
  options?: UseUserCommunitiesOptions,
) {
  return useInfiniteQuery({
    queryKey: ["userCommunities", userId, filters],
    queryFn: ({ pageParam = 1 }) =>
      getUserCommunities({
        userId,
        ...filters,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.nextPage
      }
      return undefined
    },
    enabled: !!userId && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  })
}
